export interface WSMessage {
  type: string;
  data?: any;
  message?: string;
}

enum ConnectionState {
  DISCONNECTED = 'DISCONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private state: ConnectionState = ConnectionState.DISCONNECTED;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: number | null = null;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private autoReconnect = true;
  private destroyed = false;
  public readonly instanceId: string;

  constructor(token: string) {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = import.meta.env.VITE_WS_URL?.replace(/^wss?:\/\//, '') || 'localhost:8000';
    this.url = `${wsProtocol}//${wsHost}/ws/${token}`;
    this.instanceId = `ws-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  connect(): Promise<void> {
    // Don't connect if destroyed
    if (this.destroyed) {
      return Promise.reject(new Error('WebSocket instance has been destroyed'));
    }

    // Prevent multiple simultaneous connection attempts
    if (this.state === ConnectionState.CONNECTING) {
      console.log('⏳ Connection already in progress');
      return Promise.resolve();
    }

    if (this.state === ConnectionState.CONNECTED) {
      console.log('✅ Already connected');
      return Promise.resolve();
    }

    // Clean up any existing connection
    this.cleanup(false);

    this.state = ConnectionState.CONNECTING;
    console.log('🔌 Connecting to:', this.url);

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);

        const connectionTimeout = setTimeout(() => {
          if (this.state === ConnectionState.CONNECTING) {
            console.error('❌ Connection timeout');
            this.cleanup(false);
            reject(new Error('Connection timeout'));
          }
        }, 10000); // 10 second timeout

        this.ws.onopen = () => {
          clearTimeout(connectionTimeout);
          this.state = ConnectionState.CONNECTED;
          this.reconnectAttempts = 0;
          console.log('✅ WebSocket connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error('❌ WebSocket error');
          // Don't reject here - let onclose handle it
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          const wasConnected = this.state === ConnectionState.CONNECTED;
          this.state = ConnectionState.DISCONNECTED;

          console.log(`🔌 WebSocket closed (code: ${event.code})`);

          // Only reject if we never connected
          if (!wasConnected) {
            reject(new Error(`Connection failed (code: ${event.code})`));
          }

          // Auto-reconnect if enabled and we were previously connected
          if (this.autoReconnect && wasConnected) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        this.state = ConnectionState.DISCONNECTED;
        console.error('❌ Failed to create WebSocket:', error);
        reject(error);
      }
    });
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('❌ Max reconnection attempts reached');
      return;
    }

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000); // Exponential backoff, max 30s

    console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = window.setTimeout(() => {
      this.connect().catch((err) => {
        console.error('Reconnection failed:', err);
      });
    }, delay);
  }

  private handleMessage(event: MessageEvent) {
    if (event.data instanceof Blob) {
      // Binary data (audio)
      const handler = this.messageHandlers.get('audio_chunk');
      if (handler) {
        handler(event.data);
      }
    } else {
      // JSON data
      try {
        const message: WSMessage = JSON.parse(event.data);
        console.log('🔔 Received WebSocket message:', message.type, message);
        const handler = this.messageHandlers.get(message.type);
        if (handler) {
          console.log('📨 Calling handler for:', message.type, 'with data:', message.data);
          handler(message.data || message);
        } else {
          console.warn('⚠️  No handler registered for message type:', message.type);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    }
  }

  on(messageType: string, handler: (data: any) => void) {
    this.messageHandlers.set(messageType, handler);
  }

  off(messageType: string) {
    this.messageHandlers.delete(messageType);
  }

  send(data: string | Blob) {
    if (this.state !== ConnectionState.CONNECTED || !this.ws) {
      console.warn('Cannot send message: not connected');
      return false;
    }

    try {
      this.ws.send(data);
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }

  sendText(text: string, language: string = 'en') {
    return this.send(JSON.stringify({
      type: 'text_message',
      data: { text, language }
    }));
  }

  sendAudio(audioData: Blob) {
    return this.send(audioData);
  }

  sendInteractionResponse(value: any, language: string = 'en') {
    return this.send(JSON.stringify({
      type: 'interaction_response',
      data: { value, language }
    }));
  }

  private cleanup(clearHandlers = false) {
    // Clear reconnect timeout
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Close WebSocket
    if (this.ws) {
      // Remove event handlers to prevent them from firing
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;

      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }

    if (clearHandlers) {
      this.messageHandlers.clear();
    }

    this.state = ConnectionState.DISCONNECTED;
  }

  disconnect() {
    this.autoReconnect = false;
    this.cleanup(false);
    console.log('🔌 Disconnected');
  }

  destroy() {
    this.destroyed = true;
    this.disconnect();
    this.cleanup(true);
  }

  isDestroyed(): boolean {
    return this.destroyed;
  }

  isConnected(): boolean {
    return this.state === ConnectionState.CONNECTED;
  }

  getState(): string {
    return this.state;
  }
}
